import React, { useEffect } from 'react';
import { X, Edit2, MapPin, Phone } from 'lucide-react';
import './PackageDetailsPanel.css';

export default function PackageDetailsPanel({ isOpen, onClose, packageData, onEdit }) {
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    if (isOpen) {
      window.addEventListener('keydown', handleEsc);
    }
    return () => {
      window.removeEventListener('keydown', handleEsc);
    };
  }, [isOpen, onClose]);

  if (!isOpen || !packageData) return null;

  const getStatusClass = (status) => {
    switch(status) {
      case 'IN_TRANSIT': return 'status-in-transit';
      case 'ASSIGNED': return 'status-assigned';
      case 'COMPLETED': return 'status-completed';
      case 'INCOMING': return 'status-incoming';
      default: return '';
    }
  };

  const getStatusDisplay = (status) => {
    switch(status) {
      case 'IN_TRANSIT': return 'IN TRANSIT';
      case 'ASSIGNED': return 'ASSIGNED';
      case 'COMPLETED': return 'COMPLETED';
      case 'INCOMING': return 'INCOMING';
      default: return status;
    }
  };

  return (
    <>
      <div className="panel-backdrop" onClick={onClose}></div>
      <div className="package-details-panel">
        <div className="panel-header">
          <h2>Package Details</h2>
          <div className="panel-header-actions">
            <button className="panel-edit-btn" onClick={() => onEdit && onEdit(packageData)} title="Edit Package">
              <Edit2 size={18} />
              <span>Edit</span>
            </button>
            <button className="panel-close-btn" onClick={onClose}>
              <X size={20} />
            </button>
          </div>
        </div>

        <div className="panel-content">
          <div className="panel-grid-2">
            <div>
              <p className="panel-label">ID</p>
              <p className="panel-value-bold">{packageData.id}</p>
            </div>
            <div>
              <p className="panel-label">Status</p>
              <span className={`status-badge ${getStatusClass(packageData.status)}`}>
                {getStatusDisplay(packageData.status)}
              </span>
            </div>
          </div>

          <div className="panel-section">
            <h3 className="panel-section-title">Customer / Recipient</h3>
            <div className="panel-recipient-details">
              <p className="panel-value-bold">{packageData.customer_name?.String || 'N/A'}</p>

              {packageData.delivery_address?.String && (
                <div className="panel-icon-row">
                  <MapPin size={14} className="panel-icon" />
                  <p className="panel-text-small">{packageData.delivery_address.String}</p>
                </div>
              )}

              {packageData.customer_phone?.String && (
                <div className="panel-icon-row">
                  <Phone size={14} className="panel-icon" />
                  <p className="panel-text-small">{packageData.customer_phone.String}</p>
                </div>
              )}
            </div>
          </div>

          <div className="panel-grid-2">
            <div>
              <p className="panel-label">Courier</p>
              <p className="panel-value-bold">{packageData.courier_name?.String || 'Unassigned'}</p>
            </div>
            <div>
              <p className="panel-label">Courier Phone</p>
              <p className="panel-value-bold">{packageData.courier_phone?.String || 'N/A'}</p>
            </div>
          </div>

          <div className="panel-grid-2">
            <div>
              <p className="panel-label">Weight</p>
              <p className="panel-value-bold">{packageData.total_weight ? `${packageData.total_weight} kg` : 'N/A'}</p>
            </div>
            <div>
              <p className="panel-label">Volume</p>
              <p className="panel-value-bold">{packageData.total_volume ? `${packageData.total_volume} m³` : 'N/A'}</p>
            </div>
          </div>

          <div className="panel-grid-2">
            <div>
              <p className="panel-label">Start Date</p>
              <p className="panel-value-bold">{packageData.date_start?.String || 'N/A'}</p>
            </div>
            <div>
              <p className="panel-label">End Date</p>
              <p className="panel-value-bold">{packageData.date_end?.String || 'N/A'}</p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
